export const mockScenarios = [
  "unauthenticated",
  "onboarding_incomplete",
  "empty_network",
  "failed_api",
  "no_priority_actions",
] as const;

export type MockScenario = (typeof mockScenarios)[number];

const mockScenarioSet = new Set<string>(mockScenarios);
export const mockScenarioStorageKey = "liminl.mockScenario";

const mockDevStorageKeys = [
  mockScenarioStorageKey,
  "liminl.mockGoogleConnected",
  "liminl.mockExtensionConnected",
] as const;

/** Draft + progress that would make incomplete onboarding look "already done". */
const mockOnboardingResetKeys = [
  "liminl.devOnboardingDraft",
  "liminl.chatOnboarding.progress.v1",
  "liminl.chatOnboarding.progress.v2",
  "liminl.chatOnboarding.googleOAuthHandoff.v2",
  "liminl.mockGoogleConnected",
] as const;

export function clearMockDevState() {
  if (typeof window === "undefined") return;
  for (const key of mockDevStorageKeys) {
    window.localStorage.removeItem(key);
  }
}

export function resetMockOnboardingProgress() {
  if (typeof window === "undefined") return;
  for (const key of mockOnboardingResetKeys) {
    window.localStorage.removeItem(key);
    window.sessionStorage.removeItem(key);
  }
}

export function isMockScenario(value: unknown): value is MockScenario {
  return typeof value === "string" && mockScenarioSet.has(value);
}

function readBrowserScenario() {
  if (typeof window === "undefined") return null;

  const params = new URLSearchParams(window.location.search);
  const urlScenario = params.get("liminlMock") ?? params.get("mockScenario");
  if (urlScenario === "off" || urlScenario === "clear") {
    clearMockDevState();
    return null;
  }

  if (isMockScenario(urlScenario)) {
    window.localStorage.setItem(mockScenarioStorageKey, urlScenario);
    // Fresh incomplete run whenever the URL asks for it, so signup → onboarding
    // isn't bounced to home by a leftover completed draft.
    if (urlScenario === "onboarding_incomplete") {
      resetMockOnboardingProgress();
    }
    return urlScenario;
  }

  const storedScenario = window.localStorage.getItem(mockScenarioStorageKey);
  return isMockScenario(storedScenario) ? storedScenario : null;
}

export function resolveExplicitMockScenario(): MockScenario | null {
  const browserScenario = readBrowserScenario();
  if (browserScenario) return browserScenario;

  const envScenario = import.meta.env.VITE_LIMINL_MOCK_SCENARIO;
  return isMockScenario(envScenario) ? envScenario : null;
}

export function resolveMockScenario(): MockScenario {
  return resolveExplicitMockScenario() ?? "empty_network";
}

export function shouldUseDevMockApi() {
  return import.meta.env.DEV && resolveExplicitMockScenario() !== null;
}
